import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBackend } from "../lib/backend";
import type { HeartbeatRiskStatus, HeartbeatSource } from "../lib/services";

type BluetoothCharacteristic = {
  startNotifications: () => Promise<BluetoothCharacteristic>;
  addEventListener: (type: string, listener: EventListener) => void;
  removeEventListener: (type: string, listener: EventListener) => void;
};

type BluetoothNavigator = Navigator & {
  bluetooth?: {
    requestDevice: (options: {
      filters: Array<{ services: string[] }>;
      optionalServices?: string[];
    }) => Promise<{
      gatt?: {
        connect: () => Promise<{
          getPrimaryService: (service: string) => Promise<{
            getCharacteristic: (
              characteristic: string,
            ) => Promise<BluetoothCharacteristic>;
          }>;
        }>;
      };
    }>;
  };
};

function parseHeartRate(value: DataView): number {
  const flags = value.getUint8(0);
  return flags & 0x01 ? value.getUint16(1, true) : value.getUint8(1);
}

export function usePsychAssistHeartbeat() {
  const backend = useBackend();
  const [status, setStatus] = useState<HeartbeatRiskStatus | null>(null);
  const [source, setSource] = useState<HeartbeatSource>("manual");
  const [bpm, setBpm] = useState(72);
  const [baselineBpm, setBaselineBpm] = useState(72);
  const [pnlSnapshot, setPnlSnapshot] = useState(0);
  const [loading, setLoading] = useState(true);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const characteristicRef = useRef<BluetoothCharacteristic | null>(null);
  const listenerRef = useRef<EventListener | null>(null);

  const supportsWebBluetooth =
    typeof navigator !== "undefined" &&
    Boolean((navigator as BluetoothNavigator).bluetooth);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStatus(await backend.psych.getHeartbeatStatus());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Beat status unavailable");
    } finally {
      setLoading(false);
    }
  }, [backend]);

  const recordSample = useCallback(
    async (next?: {
      bpm?: number;
      baselineBpm?: number;
      pnlSnapshot?: number;
      source?: HeartbeatSource;
      observedAt?: string;
    }) => {
      setRecording(true);
      setError(null);
      const nextBpm = next?.bpm ?? bpm;
      const nextBaseline = next?.baselineBpm ?? baselineBpm;
      const nextPnl = next?.pnlSnapshot ?? pnlSnapshot;
      const nextSource = next?.source ?? source;
      try {
        const heartbeat = await backend.psych.recordHeartbeatSample({
          bpm: nextBpm,
          baselineBpm: nextBaseline,
          pnlSnapshot: nextPnl,
          source: nextSource,
          observedAt: next?.observedAt,
        });
        setBpm(nextBpm);
        setBaselineBpm(nextBaseline);
        setPnlSnapshot(nextPnl);
        setSource(nextSource);
        setStatus(heartbeat);
        return heartbeat;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not record Beat");
        return null;
      } finally {
        setRecording(false);
      }
    },
    [backend, baselineBpm, bpm, pnlSnapshot, source],
  );

  const simulateHealthyWindow = useCallback(async () => {
    const now = Date.now();
    const samples = [
      {
        bpm: baselineBpm,
        observedAt: new Date(now - 21 * 60_000).toISOString(),
      },
      {
        bpm: baselineBpm + 2,
        observedAt: new Date(now - 11 * 60_000).toISOString(),
      },
      { bpm, observedAt: new Date(now).toISOString() },
    ];
    let last: HeartbeatRiskStatus | null = null;
    for (const sample of samples) {
      last = await recordSample({
        ...sample,
        source: "simulator",
        pnlSnapshot,
        baselineBpm,
      });
    }
    return last;
  }, [baselineBpm, bpm, pnlSnapshot, recordSample]);

  const connectWebBluetooth = useCallback(async () => {
    const bluetooth = (navigator as BluetoothNavigator).bluetooth;
    if (!bluetooth) {
      setError("Web Bluetooth is not available in this browser.");
      return false;
    }
    try {
      const device = await bluetooth.requestDevice({
        filters: [{ services: ["heart_rate"] }],
        optionalServices: ["heart_rate"],
      });
      const server = await device.gatt?.connect();
      const service = await server?.getPrimaryService("heart_rate");
      const characteristic = await service?.getCharacteristic(
        "heart_rate_measurement",
      );
      if (!characteristic) throw new Error("Heart-rate characteristic missing");
      const listener: EventListener = (event) => {
        const value = (event.target as { value?: DataView }).value;
        if (value) {
          void recordSample({
            bpm: parseHeartRate(value),
            source: "web_bluetooth",
          });
        }
      };
      characteristicRef.current = await characteristic.startNotifications();
      listenerRef.current = listener;
      characteristicRef.current.addEventListener(
        "characteristicvaluechanged",
        listener,
      );
      setSource("web_bluetooth");
      return true;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not connect monitor",
      );
      return false;
    }
  }, [recordSample]);

  useEffect(() => {
    refresh();
    return () => {
      if (characteristicRef.current && listenerRef.current) {
        characteristicRef.current.removeEventListener(
          "characteristicvaluechanged",
          listenerRef.current,
        );
      }
    };
  }, [refresh]);

  return useMemo(
    () => ({
      status,
      source,
      bpm,
      baselineBpm,
      pnlSnapshot,
      loading,
      recording,
      error,
      supportsWebBluetooth,
      setBpm,
      setBaselineBpm,
      setPnlSnapshot,
      setSource,
      refresh,
      recordSample,
      simulateHealthyWindow,
      connectWebBluetooth,
    }),
    [
      baselineBpm,
      bpm,
      connectWebBluetooth,
      error,
      loading,
      pnlSnapshot,
      recordSample,
      recording,
      refresh,
      simulateHealthyWindow,
      source,
      status,
      supportsWebBluetooth,
    ],
  );
}
